import { useCallback, useEffect, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useTranslation } from 'react-i18next';

import { $createQuoteNode, $isQuoteNode } from '@lexical/rich-text';
import { $setBlocksType } from '@lexical/selection';
import QuoteIcon from 'assets/images/icons/quote.svg?react';
import Button from 'components/button/Button';
import { RichTextEditorToolbarPluginProps } from 'components/editor/RichTextEditor.types';

interface InsertBlockquoteButtonProps {
  disabled: boolean;
  toolbarSettings: RichTextEditorToolbarPluginProps['toolbarSettings'];
}

const isQuoteActive = () => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return false;
  }

  const nodes = selection.getNodes();

  return nodes.some(
    (node) => $isQuoteNode(node) || $isQuoteNode(node.getParent()),
  );
};

const InsertBlockquoteButton = ({
  disabled,
  toolbarSettings: { features },
}: InsertBlockquoteButtonProps) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const [isBlockquote, setIsBlockquote] = useState(false);

  const $updateToolbar = useCallback(() => {
    setIsBlockquote(isQuoteActive());
  }, []);

  const handleBlockquoteClick = () => {
    editor.update(() => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) {
        return;
      }

      if (isBlockquote) {
        $setBlocksType(selection, () => $createParagraphNode());
      } else {
        $setBlocksType(selection, () => $createQuoteNode());
      }
    });
  };

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          $updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        (_payload, _newEditor) => {
          $updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, $updateToolbar]);

  if (!features?.quote) {
    return null;
  }

  return (
    <Button
      aria-label={t('form.editor.buttons.blockquote')}
      className={`toolbar-item ${isBlockquote ? 'active' : ''}`}
      data-testid="toolbar-button-quote"
      disabled={disabled}
      onClick={handleBlockquoteClick}
    >
      <QuoteIcon />
    </Button>
  );
};

export default InsertBlockquoteButton;
